import moment from 'moment';
import numeral from 'numeral';
import { v4 as uuidv4 } from 'uuid';

import { apiWithResponseHandle } from 'models/LoadingUtil';
import {
  calculateTimeRange,
  formatTimeRangeConfigDict,
  getAllMetricTable,
  getMetricTableConfig,
  updateMetricTableConfig,
} from 'services/MetricTableAPIHelper';

import { switchOneOfSelectedList } from 'utils/MultiChoiceUtils';
import { createAction } from 'utils/index';

const DATE_FORMAT = 'YYYY-MM-DD';

const parseLevelList = (levelsData) => {
  return levelsData.edges.map((edge) => {
    const { node } = edge;
    return {
      label: node?.levelName,
      value: node?.pk,
    };
  });
};

const TableMetricFieldMapping = [
  {
    label: 'Net sales (HKD)',
    field: 'netSales',
    type: 'numeric',
  },
  {
    label: 'Sold Quantity',
    field: 'soldQuantity',
    type: 'numeric',
  },
  {
    label: 'Number of transaction',
    field: 'numberOfTransaction',
    type: 'numeric',
  },
  {
    label: 'Average Transaction Value (HKD)',
    field: 'avgTransactionValue',
    type: 'numeric',
  },
  {
    label: 'Unit Per Transaction',
    field: 'unitPreTransaction',
    type: 'numeric',
  },
  {
    label: 'Average Unit Retail (HKD)',
    field: 'avgUnitRetail',
    type: 'numeric',
  },
];

const parseMetricsTablesData = (metricsTables) => {
  const metricTableData = {};
  const timeRangeList = metricsTables.tables?.[0]?.metrics?.map((metric) => [
    metric?.startDate,
    metric?.endDate,
  ]);
  metricTableData['timeRangeList'] = timeRangeList;
  const tables = [];
  const tableNames = [];
  metricsTables.tables.forEach((table, index) => {
    if (index === 0) {
      tableNames.push(null);
    } else {
      tableNames.push(table?.storeName ?? '-');
    }
    const tableData = [];
    TableMetricFieldMapping.map(({ label, field, type }) => {
      const defaultMetric = table?.metrics?.[0];
      const tableRowValues = table?.metrics?.map((metric, metricIndex) => {
        const cellData = {
          value: '-',
          tip: null,
          diffState: null,
        };
        if (metric?.[`${field}`] == null) {
          return cellData;
        }
        const cellValue = metric?.[`${field}`] ?? 0;
        const originCellValue = defaultMetric?.[`${field}`] ?? 0;
        const diffValue = numeral(cellValue).subtract(originCellValue);
        let tip = '';
        switch (type) {
          case 'numeric':
            cellData.value = numeral(cellValue).format('0,0.00');
            tip = numeral(Math.abs(diffValue.value())).format('0,0.00');
            break;
          case 'int':
            cellData.value = numeral(cellValue).format('0,0');
            tip = numeral(Math.abs(diffValue.value())).format('0,0');
            break;
          default:
            cellData.value = cellValue;
            break;
        }
        if (metricIndex === 0) {
          return cellData;
        }
        if (diffValue.value() > 0) {
          cellData.diffState = '+';
        } else if (diffValue.value() < 0) {
          cellData.diffState = '-';
        }
        if (diffValue.value() !== 0) {
          if (Number(originCellValue) === 0) {
            cellData.tip = tip + ' (-)';
          } else {
            cellData.tip =
              tip +
              ` (${numeral((diffValue.value() * 100) / originCellValue).format(
                '0,0.00',
              )}%)`;
          }
        }
        return cellData;
      });
      const tableRow = [
        {
          title: `${label}`,
        },
      ].concat(tableRowValues);
      tableData.push(tableRow);
    });
    tables.push(tableData);
  });
  metricTableData['tables'] = tables;
  metricTableData['tableNames'] = tableNames;
  return metricTableData;
};

const getInitialState = () => ({
  levels: [],
  selectedLevelIds: [],
  selectedStoreIds: [],
  timeRangeList: [],
  timeRangeConfigs: [],
  configLoading: 'start',
  metricTableData: {},
  metricTableDataLoading: false,
});

export default {
  namespace: 'metricTable',
  state: getInitialState(),
  reducers: {
    updateState(state, { payload }) {
      return { ...state, ...payload };
    },
    assembleConfig(state, { payload }) {
      const { configString = '{}' } = payload;
      const metricsTableConfig = JSON.parse(configString);
      console.log(
        '🚀 ~ MetricTableModel.js:224 ~ onSuccess ~ metricsTableConfig:',
        metricsTableConfig,
      );
      const selectedLevelIds = metricsTableConfig?.selectedLevelIds ?? [];
      const selectedStoreIds = metricsTableConfig?.selectedStoreIds ?? [];
      const timeRangeConfigs = metricsTableConfig?.timeRangeConfigs ?? [];
      if (timeRangeConfigs?.length === 0) {
        const defaultConfig = {
          name: 'Original time range',
          hash: uuidv4(),
          timeRangeType: 0,

          timeRangeCustomStart: moment()
            .subtract(7, 'days')
            .format(DATE_FORMAT),
          timeRangeCustomEnd: moment().format(DATE_FORMAT),
          timeRangeLastType: null,
          timeRangeToDateType: null,
          timeRangeWeekX: null,
          timeRangeWeekYear: null,
        };
        timeRangeConfigs.push(defaultConfig);
      }
      console.log(
        '🚀 ~ MetricTableModel.js:192 ~ assembleConfig ~ timeRangeConfigs:',
        timeRangeConfigs,
      );
      const allNotPeriodHash = timeRangeConfigs
        ?.filter(
          (config) =>
            Number(config?.timeRangeType) >= 0 &&
            Number(config?.timeRangeType) < 4,
        )
        ?.map((config) => config?.hash);
      const filteredTimeRangeConfigs = timeRangeConfigs?.filter(
        (config) =>
          Number(config?.timeRangeType) !== 4 ||
          allNotPeriodHash.includes(config?.timeRangePeriodLastHash),
      );
      console.log(
        '🚀 ~ MetricTableModel.js:202 ~ assembleConfig ~ filteredTimeRangeConfigs:',
        filteredTimeRangeConfigs,
      );
      const timeRangeList = filteredTimeRangeConfigs?.map((config) => {
        if (config?.timeRangeType === 0) {
          return [config.timeRangeCustomStart, config.timeRangeCustomEnd];
        }
        if (config?.timeRangeType < 4) {
          const { start, end } = calculateTimeRange(config);
          return [
            moment(start).format(DATE_FORMAT),
            moment(end).format(DATE_FORMAT),
          ];
        }
        if (config?.timeRangeType === 4) {
          const originConfig = filteredTimeRangeConfigs?.find(
            (x) =>
              x.hash === config?.timeRangePeriodLastHash && x.timeRangeType < 4,
          );
          const originResult = calculateTimeRange(originConfig);
          const timeRangePeriodStartDate = originResult?.start;
          const timeRangePeriodEndDate = originResult?.end;
          const { start, end } = calculateTimeRange({
            timeRangeType: config?.timeRangeType,
            timeRangePeriodStartDate,
            timeRangePeriodEndDate,
            timeRangePeriodLastType: config?.timeRangePeriodLastType,
          });
          return [
            moment(start).format(DATE_FORMAT),
            moment(end).format(DATE_FORMAT),
          ];
        }
        return [moment().format(DATE_FORMAT), moment().format(DATE_FORMAT)];
      });
      return {
        ...state,
        selectedLevelIds,
        selectedStoreIds,
        timeRangeConfigs: filteredTimeRangeConfigs,
        timeRangeList,
      };
    },
    resetState(state) {
      return getInitialState();
    },
  },
  effects: {
    getMetricTableConfig: [
      function* ({ payload }, { call, put, select }) {
        yield put(
          createAction('updateState')({
            configLoading: 'start',
          }),
        );
        const serviceArgs = [getMetricTableConfig];
        function* onSuccess(data) {
          const levels = parseLevelList(data.levels);
          yield put(
            createAction('updateState')({
              levels,
            }),
          );
          yield put(
            createAction('updateState')({
              levels,
            }),
          );

          const configString = data?.metricsTableConfig?.metricsTableConfig;
          yield put(
            createAction('assembleConfig')({
              configString,
            }),
          );

          yield put(
            createAction('updateState')({
              configLoading: 'success',
            }),
          );
        }
        function* onFailed(data) {
          yield put(
            createAction('updateState')({
              configLoading: 'failed',
            }),
          );
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
    updateMetricTableConfig: [
      function* ({ payload }, { call, put, select }) {
        let { selectedLevelIds, selectedStoreIds, timeRangeConfigs } = payload;
        const { successAction = () => {} } = payload;
        if (!selectedLevelIds) {
          selectedLevelIds = yield select(
            (state) => state.metricTable.selectedLevelIds,
          );
        }
        if (!selectedStoreIds) {
          selectedStoreIds = yield select(
            (state) => state.metricTable.selectedStoreIds,
          );
        }
        if (!timeRangeConfigs) {
          timeRangeConfigs = yield select(
            (state) => state.metricTable.timeRangeConfigs,
          );
        }
        const config = {
          selectedLevelIds,
          selectedStoreIds,
          timeRangeConfigs:
            timeRangeConfigs?.map((config) =>
              formatTimeRangeConfigDict(config),
            ) ?? [],
        };
        const configString = JSON.stringify(config);
        const serviceArgs = [updateMetricTableConfig, configString];
        function* onSuccess(data) {
          const configString =
            data?.updateMetricsTableConfig?.node?.metricsTableConfig;
          yield put(
            createAction('assembleConfig')({
              configString,
            }),
          );
          yield successAction();
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
    getMetricTableData: [
      function* ({ payload }, { call, put, select }) {
        yield put(
          createAction('updateState')({
            metricTableDataLoading: true,
          }),
        );
        const selectedLevelIds = yield select(
          (state) => state.metricTable.selectedLevelIds,
        );
        const selectedStoreIds = yield select(
          (state) => state.metricTable.selectedStoreIds,
        );
        const timeRangeList = yield select(
          (state) => state.metricTable.timeRangeList,
        );
        const startDates = timeRangeList?.map(
          (timeRange) => timeRange?.[0] ?? null,
        );
        const endDates = timeRangeList?.map(
          (timeRange) => timeRange?.[1] ?? null,
        );
        const serviceArgs = [
          getAllMetricTable,
          {
            selectedLevelIds,
            selectedStoreIds,
            startDates,
            endDates,
          },
        ];
        function* onSuccess(data) {
          console.log('getMetricTableData@25', data.metricsTables);
          const metricTableData = parseMetricsTablesData(data.metricsTables);
          const { timeRangeList = [] } = metricTableData;
          yield put(
            createAction('updateState')({
              metricTableData,
              metricTableDataLoading: false,
            }),
          );
        }
        function* onFailed(data) {
          console.log('getMetricTableData@28', data);
          yield put(
            createAction('updateState')({
              metricTableDataLoading: false,
            }),
          );
        }
        yield apiWithResponseHandle(serviceArgs, onSuccess, onFailed);
      },
      { type: 'takeLatest' },
    ],
  },
};
